import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import UpdateBanner from "@/components/UpdateBanner";
import ErrorBoundary from "@/components/ErrorBoundary";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <UpdateBanner />
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);

// On dev/preview hosts, never cache: unregister any existing SW and clear caches
// so code changes show up immediately (phone + browser) without manual cache-busting.
// 2026-09-09: a bare private LAN address (10.x, 192.168.x, 172.16-31.x) counts as
// a dev host too — it is the documented same-wifi test setup, and without this
// the app registered the worker as if in production there and pinned old code.
// Keep this list identical to _DEV_HOST in public/sw.js.
const _isDevHost =
  typeof window !== 'undefined' &&
  (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) ||
    /^(dev|test|staging|preview)[a-z0-9-]*\./i.test(window.location.hostname) ||
    /^(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/.test(window.location.hostname));

// Register Service Worker for PWA + Offline support
if (_isDevHost && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
  if (window.caches) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
} else if ('serviceWorker' in navigator) {
  // ── USER-CONTROLLED UPDATE FLOW (owner decision 2026-07-17) ──────────────
  // The SW's offline cache means a fresh deploy does NOT take over running
  // sessions (good — a forced reload could wipe a cart mid-sale). Instead:
  // a new version installs in the background and WAITS; we announce it via
  // `agribooks:update-ready` and <UpdateBanner/> shows "Update now". Only the
  // click activates the waiting worker (SKIP_WAITING) and reloads. sw.js no
  // longer skipWaiting()s on install — activation is the user's call.
  const announceWhenWaiting = (registration) => {
    let announced = false;
    const announce = () => {
      if (announced || !registration.waiting) return;
      announced = true;
      // Park it for the pre-mount race, then broadcast for the banner.
      window.__agribooksUpdateReady = registration;
      window.dispatchEvent(new CustomEvent('agribooks:update-ready', { detail: { registration } }));
    };
    if (registration.waiting) announce(); // deploy happened while tab was closed
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        // 'installed' + an existing controller = a NEW version is waiting.
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) announce();
      });
    });
    // Long-running POS terminals never navigate — poll for new builds every
    // 30 min and whenever the tab regains focus, so the banner shows up
    // without anyone reloading.
    const check = () => { registration.update().catch(() => {}); };
    setInterval(check, 30 * 60 * 1000);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
  };

  // Called by UpdateBanner's "Update now": activate the waiting worker, then
  // reload ONCE when it takes control. Never runs without the user's click.
  window.__agribooksApplyUpdate = (registration) => {
    if (!registration || !registration.waiting) { window.location.reload(); return; }
    let reloaded = false;
    const reloadOnce = () => { if (!reloaded) { reloaded = true; window.location.reload(); } };
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(reloadOnce, 4000); // safety net if controllerchange never fires
  };

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
        announceWhenWaiting(registration);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// PWA fullscreen — request on first user interaction when running as installed app.
// The manifest sets display:"fullscreen" which handles Android natively.
// Desktop Chrome needs a gesture before Fullscreen API is allowed.
(function pwaFullscreen() {
  const isPwa =
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  if (!isPwa) return;

  function requestFs() {
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
    }
    cleanup();
  }
  function cleanup() {
    ['click', 'keydown', 'touchstart'].forEach(e => document.removeEventListener(e, requestFs));
  }
  // Try immediately (works on some desktop Chromium builds for installed PWAs)
  if (document.documentElement.requestFullscreen) {
    document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => {
      // Gesture required — attach listeners for first interaction
      ['click', 'keydown', 'touchstart'].forEach(e =>
        document.addEventListener(e, requestFs, { once: true, passive: true })
      );
    });
  }
})();
